import React, { useEffect } from 'react'
import Button from '@mui/material/Button';
import { useFormik } from "formik";
import * as Yup from "yup";
import { Box, Divider, TextField, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import CommentDrawer from '../Common/Drawer';
import { fetchRedditAC, createRedditAC, updateRedditAC } from '../../store/cookieSlice';
import { toast } from 'react-toastify';
import PasswordVisibility from '../Common/PasswordVisibility';
import { decryptValue, encrypt } from '../../utils/helper';

export default function RedditForm({ title, redditModal, setRedditModal, selecteReddit = null, getReddits }) {
    const dispatch = useDispatch();

    const validationSchema = Yup.object({
        app_name: Yup.string().required("App Name is required"),
        client_id: Yup.string().required("Client ID is required"),
        client_secret: Yup.string().required("Client Secret is required"),
    });

    const formik = useFormik({
        initialValues: {
            app_name: "",
            client_id: "",
            client_secret: ""
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { resetForm }) => {
            setRedditModal(false);
            const toastId = toast.loading("We're processing the Reddit credentials. This may take a few moments");
            try {
                // Encrypt client_id and client_secret before sending
                const encryptedClientId = encrypt(values.client_id);
                const encryptedClientSecret = encrypt(values.client_secret);
                const payload = {
                    ...values,
                    client_id: encryptedClientId,
                    client_secret: encryptedClientSecret
                };
                if (selecteReddit && selecteReddit.id) {
                    // Edit
                    const data = { ...payload, id: selecteReddit.uuid };
                    await dispatch(updateRedditAC({ id: selecteReddit.uuid, values: data })).then(() => {
                        if (getReddits) getReddits();
                    });
                } else {
                    // Create
                    await dispatch(createRedditAC(payload)).then(() => {
                        if (getReddits) getReddits();
                    });
                }
                toast.success("Action completed successfully", { toastId });
                resetForm();
            } catch (error) {
                toast.error("Something went wrong. Please try again.", { toastId });
            } finally {
                toast.dismiss(toastId);
            }
        }
    });

    // Separate show/hide for client_id and client_secret
    const [showClientId, setShowClientId] = React.useState(false);
    const [showClientSecret, setShowClientSecret] = React.useState(false);

    useEffect(() => {
        if (redditModal) {
            if (selecteReddit) {
                formik.setValues({
                    app_name: selecteReddit.app_name || "",
                    client_id: selecteReddit?.client_id ? decryptValue(selecteReddit.client_id) : "",
                    client_secret: selecteReddit?.client_secret ? decryptValue(selecteReddit.client_secret) : ""
                });
            } else {
                formik.resetForm();
            }
        }
        // eslint-disable-next-line
    }, [redditModal, selecteReddit]);

    return (
        <CommentDrawer open={redditModal} setOpen={setRedditModal}>
            <Box className="p-3 position-relative h-100">
                <form onSubmit={formik.handleSubmit}>
                    <Typography variant="h6" gutterBottom>
                        {title}
                    </Typography>
                    <Divider />
                    <TextField
                        fullWidth
                        required
                        id="app_name"
                        name="app_name"
                        label="Reddit App Name"
                        margin="normal"
                        variant="outlined"
                        placeholder="Enter Reddit App Name"
                        value={formik.values.app_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.app_name && Boolean(formik.errors.app_name)}
                        helperText={formik.touched.app_name && formik.errors.app_name}
                    />
                    <TextField
                        fullWidth
                        required
                        id="client_id"
                        name="client_id"
                        label="Client ID"
                        margin="normal"
                        variant="outlined"
                        placeholder="Enter Client ID"
                        value={formik.values.client_id}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.client_id && Boolean(formik.errors.client_id)}
                        helperText={formik.touched.client_id && formik.errors.client_id}
                        type={showClientId ? "text" : "password"}
                        InputProps={{
                            endAdornment: (
                                <PasswordVisibility
                                    showPassword={showClientId}
                                    onToggle={() => setShowClientId((prev) => !prev)}
                                />
                            )
                        }}
                    />
                    <TextField
                        fullWidth
                        required
                        id="client_secret"
                        name="client_secret"
                        label="Client Secret"
                        margin="normal"
                        variant="outlined"
                        placeholder="Enter Client Secret"
                        value={formik.values.client_secret}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.client_secret && Boolean(formik.errors.client_secret)}
                        helperText={formik.touched.client_secret && formik.errors.client_secret}
                        type={showClientSecret ? "text" : "password"}
                        InputProps={{
                            endAdornment: (
                                <PasswordVisibility
                                    showPassword={showClientSecret}
                                    onToggle={() => setShowClientSecret((prev) => !prev)}
                                />
                            )
                        }}
                    />
                    <Box className="drawer-footer m-3">
                        <Button
                            type="button"
                            variant="outlined"
                            color="primary"
                            onClick={() => setRedditModal(false)}
                            className="mt-2 mr-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className="mt-2"
                            disabled={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                        >
                            Save
                        </Button>
                    </Box>
                </form>
            </Box>
        </CommentDrawer>
    );
}